// Delete-draft confirmation modal (Phase 5A UI polish · draft cleanup).
//
// Only offered for an all-draft, never-published policy with no audience. Shows
// the policy name so the admin confirms exactly what is removed. The backend is
// the real safety gate (published/retired/applicability/attempt/acceptance → 409).

import { useState } from 'react'
import { AlertTriangleIcon } from 'lucide-react'
import { DialogContent } from '../../../components/ui/dialog'
import { Dialog } from '../../../components/ui/dialog.lib'
import { Button } from '../../../components/ui/button'
import { AgreementsError } from '../../../api/agreements'
import * as api from '../../../api/agreementsAdmin'
import { useT } from '../../../i18n'
import { policiesI18n } from '../../../i18n/admin/policies'

export function DeleteDraftModal({
  agreementId,
  policyName,
  onClose,
  onDeleted,
}: {
  agreementId: string
  policyName: string
  onClose: () => void
  onDeleted: () => void | Promise<void>
}) {
  const t = useT(policiesI18n)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      await api.deleteDraftPolicy(agreementId)
      await onDeleted()
      onClose()
    } catch (e) {
      setError(e instanceof AgreementsError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md gap-0 p-0">
        <div className="flex items-start gap-3 px-5 py-4">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600">
            <AlertTriangleIcon className="h-4 w-4" />
          </span>
          <div>
            <h2 className="text-base font-semibold text-slate-900">{t.deleteTitle}</h2>
            <p className="mt-1 text-sm text-slate-600">
              {t.deleteBody}{' '}
              <span className="font-medium text-slate-900">{policyName}</span>
            </p>
            <p className="mt-1 text-xs text-slate-400">{t.deleteHint}</p>
          </div>
        </div>
        {error && (
          <div className="mx-5 mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            {t.cancel}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={confirm}
            disabled={busy}
            data-testid="confirm-delete-btn"
          >
            {t.deleteConfirm}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
